import { 
  collection, 
  addDoc, 
  updateDoc, 
  doc, 
  getDoc, 
  getDocs, 
  query, 
  where, 
  onSnapshot,
  serverTimestamp,
  orderBy
} from 'firebase/firestore';
import { db } from '../firebase';
import { notificationService } from './notificationService';

export const bidService = {
  // Check if provider already has a bid on a ticket
  async checkExistingBid(ticketId, providerId) {
    try {
      const q = query(
        collection(db, 'bids'),
        where('ticketId', '==', ticketId),
        where('providerId', '==', providerId)
      );
      const querySnapshot = await getDocs(q);
      return !querySnapshot.empty;
    } catch (error) {
      console.error('Error checking existing bid:', error);
      return false;
    }
  },

  // Get existing bid by provider for a specific ticket
  async getExistingBid(ticketId, providerId) {
    try {
      const q = query(
        collection(db, 'bids'),
        where('ticketId', '==', ticketId),
        where('providerId', '==', providerId)
      );
      const querySnapshot = await getDocs(q);
      if (!querySnapshot.empty) {
        const doc = querySnapshot.docs[0];
        return { id: doc.id, ...doc.data() };
      }
      return null;
    } catch (error) {
      console.error('Error getting existing bid:', error);
      return null;
    }
  },

  // Create a new bid
  async createBid(bidData) {
    try {
      // Check if provider already has a bid on this ticket
      const hasExistingBid = await this.checkExistingBid(bidData.ticketId, bidData.providerId);
      if (hasExistingBid) {
        throw new Error('You have already submitted a bid for this ticket. Only one bid per ticket is allowed.');
      }

      const docRef = await addDoc(collection(db, 'bids'), {
        ...bidData,
        status: 'pending', // pending, approved, rejected
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });

      // Notify the ticket owner about the new bid
      try {
        const ticket = await getDoc(doc(db, 'tickets', bidData.ticketId));
        if (ticket.exists()) {
          const ticketData = ticket.data();
          await notificationService.createNotification({
            recipientId: ticketData.ownerId,
            type: 'new_bid',
            title: `New bid received for ticket: ${ticketData.title || 'Maintenance Request'}`,
            body: `A service provider has submitted a bid for ${bidData.amount ? `$${bidData.amount}` : 'your maintenance request'}`,
            resourceType: 'ticket',
            resourceId: bidData.ticketId,
            bidId: docRef.id
          });
        }
      } catch (e) {
        console.error('Failed to create bid notification:', e);
      }

      return docRef.id;
    } catch (error) {
      throw error;
    }
  },

  // Get all bids (Admin function)
  async getAllBids() {
    try {
      const bidsCollection = collection(db, 'bids');
      const bidsSnapshot = await getDocs(bidsCollection);
      const bids = [];
      
      bidsSnapshot.forEach((doc) => {
        bids.push({
          id: doc.id,
          ...doc.data()
        });
      });
      
      return bids;
    } catch (error) {
      console.error('Error getting all bids:', error);
      throw error;
    }
  },

  // Get bid by ID
  async getBid(bidId) {
    try {
      const bidDoc = await getDoc(doc(db, 'bids', bidId));
      if (bidDoc.exists()) {
        return { id: bidDoc.id, ...bidDoc.data() };
      }
      return null;
    } catch (error) {
      throw error;
    }
  },

  // Update bid
  async updateBid(bidId, updates) {
    try {
      const bidRef = doc(db, 'bids', bidId);
      await updateDoc(bidRef, {
        ...updates,
        updatedAt: serverTimestamp()
      });
    } catch (error) {
      throw error;
    }
  },

  // Get all bids for a ticket
  async getBidsByTicket(ticketId) {
    try {
      const q = query(
        collection(db, 'bids'),
        where('ticketId', '==', ticketId),
        orderBy('createdAt', 'desc')
      );
      const querySnapshot = await getDocs(q);
      const bids = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      // Enhance bids with provider information if missing
      const enhancedBids = await Promise.all(
        bids.map(async (bid) => {
          if (!bid.providerName || !bid.providerEmail) {
            try {
              const providerDoc = await getDoc(doc(db, 'users', bid.providerId));
              if (providerDoc.exists()) {
                const providerData = providerDoc.data();
                return {
                  ...bid,
                  providerName: bid.providerName || providerData.displayName || providerData.name || 'Service Provider',
                  providerEmail: bid.providerEmail || providerData.email || '',
                  providerCompany: bid.providerCompany || providerData.company || ''
                };
              }
            } catch (error) {
              console.error('Error fetching provider data for bid:', bid.id, error);
            }
          }
          return bid;
        })
      );
      
      return enhancedBids;
    } catch (error) {
      throw error;
    }
  },

  // Get bids by service provider
  async getBidsByProvider(providerId) {
    try {
      const q = query(
        collection(db, 'bids'),
        where('providerId', '==', providerId),
        orderBy('createdAt', 'desc')
      );
      const querySnapshot = await getDocs(q);
      const bids = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      return bids;
    } catch (error) {
      throw error;
    }
  },

  // Approve a bid
  async approveBid(bidId, ticketId) {
    try {
      // Update bid status
      await this.updateBid(bidId, { status: 'approved' });

      // Get the bid details
      const bid = await this.getBid(bidId);
      if (!bid) throw new Error('Bid not found');

      // Update ticket with approved provider
      const ticketRef = doc(db, 'tickets', ticketId);
      await updateDoc(ticketRef, {
        serviceProviderIds: [bid.providerId],
        status: 'in-progress',
        updatedAt: serverTimestamp()
      });

      // Notify the service provider
      try {
        await notificationService.createNotification({
          recipientId: bid.providerId,
          type: 'bid_approved',
          title: 'Your bid has been approved!',
          body: 'The property owner has approved your bid. You can now start working on the ticket.',
          resourceType: 'ticket',
          resourceId: ticketId,
          bidId: bidId
        });
      } catch (e) {
        console.error('Failed to create approval notification:', e);
      }

      // Reject all other bids for this ticket
      const allBids = await this.getBidsByTicket(ticketId);
      const otherBids = allBids.filter(b => b.id !== bidId && b.status === 'pending');
      
      for (const otherBid of otherBids) {
        await this.updateBid(otherBid.id, { status: 'rejected' });
        
        // Notify other providers
        try {
          await notificationService.createNotification({
            recipientId: otherBid.providerId,
            type: 'bid_rejected',
            title: 'Bid status update',
            body: 'Another service provider was selected for this maintenance request.',
            resourceType: 'ticket',
            resourceId: ticketId,
            bidId: otherBid.id
          });
        } catch (e) {
          console.error('Failed to create rejection notification:', e);
        }
      }
    } catch (error) {
      throw error;
    }
  },

  // Reject a bid
  async rejectBid(bidId) {
    try {
      await this.updateBid(bidId, { status: 'rejected' });

      // Notify the service provider
      const bid = await this.getBid(bidId);
      if (bid) {
        try {
          await notificationService.createNotification({
            recipientId: bid.providerId,
            type: 'bid_rejected',
            title: 'Bid status update',
            body: 'Your bid was not selected for this maintenance request.',
            resourceType: 'ticket',
            resourceId: bid.ticketId,
            bidId: bidId
          });
        } catch (e) {
          console.error('Failed to create rejection notification:', e);
        }
      }
    } catch (error) {
      throw error;
    }
  },

  // Subscribe to bids for a ticket
  subscribeToBidsByTicket(ticketId, callback) {
    try {
      // Use a simple query without orderBy to avoid index issues
      const q = query(
        collection(db, 'bids'),
        where('ticketId', '==', ticketId)
      );
      
      return onSnapshot(q, async (snap) => {
        const bids = snap.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        
        // Sort manually to avoid index requirements
        bids.sort((a, b) => {
          if (a.createdAt && b.createdAt) {
            return b.createdAt.toDate() - a.createdAt.toDate();
          }
          return 0;
        });
        
        // Enhance bids with provider information if missing
        const enhancedBids = await Promise.all(
          bids.map(async (bid) => {
            if (!bid.providerName || !bid.providerEmail) {
              try {
                const providerDoc = await getDoc(doc(db, 'users', bid.providerId));
                if (providerDoc.exists()) {
                  const providerData = providerDoc.data();
                  return {
                    ...bid,
                    providerName: bid.providerName || providerData.displayName || providerData.name || 'Service Provider',
                    providerEmail: bid.providerEmail || providerData.email || '',
                    providerCompany: bid.providerCompany || providerData.company || ''
                  };
                }
              } catch (error) {
                console.error('Error fetching provider data for bid:', bid.id, error);
              }
            }
            return bid;
          })
        );
        
        callback(enhancedBids);
      });
    } catch (error) {
      console.error('Failed to subscribe to bids:', error);
      callback([]);
      return () => {};
    }
  },

  // Subscribe to bids by provider
  subscribeToBidsByProvider(providerId, callback) {
    try {
      const q = query(
        collection(db, 'bids'),
        where('providerId', '==', providerId),
        orderBy('createdAt', 'desc')
      );
      return onSnapshot(q, (snap) => {
        const bids = snap.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        callback(bids);
      });
    } catch (error) {
      console.error('Failed to subscribe to provider bids:', error);
      callback([]);
      return () => {};
    }
  }
};
